import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { isChangePasswordUrl } from '../change-password/change-password.route';
import { deadlineFromPayload } from '../password-prompt/password-deadline';
import { PasswordPromptComponent } from '../password-prompt/password-prompt.component';
import { PasswordPromptService } from '../password-prompt/password-prompt.service';
import { Announcement, ANNOUNCEMENT_KEYS } from './announcement.model';
import { AnnouncementsService } from './announcements.service';

/**
 * Renders the server's due announcements (ZEY-3181) that this host knows. The payment reminder is rendered by the
 * payment popup, which reads the same service; any other unknown key is ignored.
 *
 * Until the API sends announcements (older API), it falls back to the ZEY-3140 password check. Nothing shows on
 * /change-password, which is the same form as a page.
 */
@Component({
  selector: 'app-announcement-host',
  standalone: true,
  imports: [PasswordPromptComponent],
  template: `<app-password-prompt
    [open]="!onChangePasswordPage && (!!setPassword || legacyPasswordRequired)"
    [snoozesLeft]="setPassword ? setPassword.snoozesLeft : 0"
    [blocking]="setPassword ? setPassword.blocking : true"
    [snoozeError]="snoozeError"
    [deadline]="deadline"
    (snoozed)="onSnooze()"
    (completed)="onPasswordSet()"
    (deferred)="onPasswordDeferred()"
  ></app-password-prompt>`
})
export class AnnouncementHostComponent implements OnInit {
  private readonly announcements = inject(AnnouncementsService);
  private readonly passwordPrompt = inject(PasswordPromptService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  setPassword: Announcement | null = null;
  legacyPasswordRequired = false;
  snoozeError = false;
  /** The /change-password page is this form already: never show the popup on top of it. */
  onChangePasswordPage = false;
  private legacyChecked = false;

  /** The countdown's date, from the announcement's payload `{ "deadline": "YYYY-MM-DD" }`. */
  get deadline(): string | null {
    return deadlineFromPayload(this.setPassword?.payload);
  }

  ngOnInit(): void {
    this.onChangePasswordPage = isChangePasswordUrl(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.onChangePasswordPage = isChangePasswordUrl(e.urlAfterRedirects);
        this.cdr.markForCheck();
      });

    combineLatest([this.announcements.supported$, this.announcements.find$(ANNOUNCEMENT_KEYS.setPassword)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([supported, setPassword]) => {
        this.setPassword = setPassword;
        if (supported === true) {
          this.legacyPasswordRequired = false;
        } else if (supported === false) {
          this.checkLegacyOnce();
        }
        // null: the current user hasn't arrived yet. Wait, or a popup the list leaves out flashes up.
        this.cdr.markForCheck();
      });
  }

  async onSnooze(): Promise<void> {
    if (!this.setPassword) return;
    this.snoozeError = false;
    try {
      await this.announcements.snooze(this.setPassword);
    } catch {
      this.snoozeError = true;
      // The server's count is the truth: re-read it (it may now be blocking).
      await this.announcements.refresh();
    }
    this.cdr.markForCheck();
  }

  onPasswordSet(): void {
    this.legacyPasswordRequired = false;
    this.snoozeError = false;
    this.announcements.drop(ANNOUNCEMENT_KEYS.setPassword);
    this.cdr.markForCheck();
  }

  /** ZEY-3140: password change isn't available on our side; the server already put the prompt off, so hide it. */
  onPasswordDeferred(): void {
    this.legacyPasswordRequired = false;
    this.snoozeError = false;
    this.announcements.drop(ANNOUNCEMENT_KEYS.setPassword);
    this.cdr.markForCheck();
  }

  // An API without announcements: ask the ZEY-3140 status once, as the old host did.
  private checkLegacyOnce(): void {
    if (this.legacyChecked) return;
    this.legacyChecked = true;
    this.passwordPrompt
      .isRequired()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((required) => {
        this.legacyPasswordRequired = required && !this.announcements.supported;
        this.cdr.markForCheck();
      });
  }
}
